import React, { useMemo } from 'react';
import { Grid, Typography, useTheme } from '@mui/material';
import { COLOR_PRIMARY_1, COLOR_PRIMARY_1_700, COLOR_TEXT_2 } from 'src/utils/colors';
import { getHeadingStyle, getSupplyByPropertySize } from 'src/utils/functions';
import { DynamicBarChart } from 'src/App/Dashboards/Market/Components/Charts';
import { InfoButton } from 'src/App/Dashboards/Market/Components/Buttons';
import { LoadingCard } from 'src/App/Dashboards/Market/Components';
import NoDataCard from 'src/App/Dashboards/Market/Components/Cards/NoDataCard';

const PropertySizeCard = ({
  cachedData,
  viewMode,
  isDynamicLoading,
  multipleMarkersCallLoading,
  noDataInMarket,
  totalListingsCount,
  listingByPropertySizeGraphRef,
}: {
  cachedData: { [key: string]: number } | null | undefined;
  viewMode: 'map' | 'dashboard' | 'discovery' | 'compare' | 'advance' | 'searchListing';
  isDynamicLoading: boolean;
  multipleMarkersCallLoading: Boolean;
  noDataInMarket: boolean;
  totalListingsCount: number;
  listingByPropertySizeGraphRef: any;
}) => {
  const theme = useTheme();

  const isDataStatic = viewMode === 'dashboard' && cachedData;
  const isDataLoading = multipleMarkersCallLoading && !isDataStatic;

  const supplyByPropertySize = useMemo(() => {
    if (isDataLoading) {
      return [];
    }
    return getSupplyByPropertySize(cachedData);
  }, [cachedData]);

  if (isDataLoading || isDynamicLoading) {
    return <LoadingCard height={150} type="bars" />;
  }

  if (noDataInMarket) {
    return <NoDataCard height={150} borderRadius={8} />;
  }

  const getTooltipSuffix = (value: number, key: string) => {
    const percentageOfTotalListings = Math.round((value / Math.max(1, totalListingsCount)) * 100);
    return `${value} listings ( ${
      percentageOfTotalListings ? percentageOfTotalListings + '% ' : 'less than 1% '
    } )`;
  };

  return (
    <Grid
      container
      direction="column"
      justifyContent="center"
      item
      xs={12}
      sx={{ boxShadow: '0 4px 10px #0003', borderRadius: 1, ...theme.utils.cardPadding }}
    >
      <Grid container item sx={{ width: '100%' }}>
        <Typography variant="body1" sx={getHeadingStyle(COLOR_TEXT_2)}>
          Number of Listings by Property Size
          <InfoButton tooltipContent="This data only includes entire place listings on Airbnb." />
        </Typography>

        <div style={{ width: '100%', height: '100%' }} ref={listingByPropertySizeGraphRef}>
          <DynamicBarChart
            layout="horizontal"
            data={supplyByPropertySize}
            unit="listings"
            label="Bedrooms"
            height={150}
            barColor={COLOR_PRIMARY_1_700}
            barAccentColor={COLOR_PRIMARY_1}
            useShortHand={true}
            isPropertySizeGraph={true}
            getTooltipText={getTooltipSuffix}
            parent="listing_by_property_size"
          />
        </div>
      </Grid>
    </Grid>
  );
};

export default React.memo(PropertySizeCard);
